import React from 'react';
import PropTypes from 'prop-types';

import {
  DATA_TABLE_SOURCE_JOB_ORDER_NOTES,
  DATA_TABLE_SOURCE_LISTING_NOTES,
  DATA_TABLE_VIEW_ID_JOB_ORDER_NOTES,
  DATA_TABLE_VIEW_ID_LISTING_NOTES_ALL,
} from 'common/dataTableConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrCreateNoteForm, {
  SR_CREATE_NOTE_FORM_THEME_ADOBE,
} from 'client/components/SrCreateNoteForm';
import SrNotesList, {
  SR_JOB_ORDER_NOTES,
  SR_LISTING_NOTES,
  SR_LISTING_JOB_ORDER_NOTES,
  SR_NOTES_LIST_THEME_GREYCLIFF,
} from 'client/components/SrNotesList';

export const ADMIN_NOTES_VARIATION_LISTING_NOTES = 'ADMIN_NOTES_VARIATION_LISTING_NOTES';
export const ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES = 'ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES';
export const ADMIN_NOTES_VARIATION_LISTING_JOB_ORDER_NOTES =
  'ADMIN_NOTES_VARIATION_LISTING_JOB_ORDER_NOTES';

const ADMIN_NOTES_VARIATIONS = [
  ADMIN_NOTES_VARIATION_LISTING_NOTES,
  ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES,
];

const SrAdminNotes = ({
  listingId,
  jobOrderId,
  variation,
  dispatch,
  currentUser,
  onNoteCreated,
  onNoteDeleted,
}) => {
  const _getAdminNotesDataTableConfiguration = ({ listingId, jobOrderId, variation }) => {
    switch (variation) {
      case ADMIN_NOTES_VARIATION_LISTING_NOTES:
        return {
          itemsPropName: 'notes',
          filterId: 'listingNotes.id',
          rootViewId: DATA_TABLE_VIEW_ID_LISTING_NOTES_ALL,
          dataSource: DATA_TABLE_SOURCE_LISTING_NOTES,
          initialViewSettings: {
            sort: {
              createdDate: 'desc',
            },
            filter: {
              listingId,
            },
          },
        };
      case ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES:
        return {
          itemsPropName: 'notes',
          filterId: 'jobOrderNotes.id',
          rootViewId: DATA_TABLE_VIEW_ID_JOB_ORDER_NOTES,
          dataSource: DATA_TABLE_SOURCE_JOB_ORDER_NOTES,
          initialViewSettings: {
            sort: {
              createdDate: 'desc',
            },
            filter: {
              jobOrderId,
            },
          },
        };
      case ADMIN_NOTES_VARIATION_LISTING_JOB_ORDER_NOTES:
        return {
          itemsPropName: 'notes',
          filterId: 'jobOrderNotes.listingId',
          rootViewId: `${DATA_TABLE_VIEW_ID_JOB_ORDER_NOTES}_LISTING_${listingId}`,
          dataSource: DATA_TABLE_SOURCE_JOB_ORDER_NOTES,
          initialViewSettings: {
            sort: {
              createdDate: 'desc',
            },
            filter: {
              listingId,
            },
          },
        };
      default:
        return {};
    }
  };

  const _getNotesListConfiguration = ({ variation }) => {
    switch (variation) {
      case ADMIN_NOTES_VARIATION_LISTING_NOTES:
        return {
          type: SR_LISTING_NOTES,
          theme: SR_NOTES_LIST_THEME_GREYCLIFF,
        };
      case ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES:
        return {
          type: SR_JOB_ORDER_NOTES,
          theme: SR_NOTES_LIST_THEME_GREYCLIFF,
        };
      case ADMIN_NOTES_VARIATION_LISTING_JOB_ORDER_NOTES:
        return {
          type: SR_LISTING_JOB_ORDER_NOTES,
          theme: SR_NOTES_LIST_THEME_GREYCLIFF,
        };
      default:
        return {};
    }
  };

  const _shouldDisplayCreateNoteForm = ({ variation }) => {
    switch (variation) {
      case ADMIN_NOTES_VARIATION_LISTING_JOB_ORDER_NOTES:
        return false;
      default:
        return true;
    }
  };

  const dataTableConfiguration = _getAdminNotesDataTableConfiguration({
    listingId,
    jobOrderId,
    variation,
  });

  const notesListConfiguration = _getNotesListConfiguration({ variation });
  const shouldDisplayCreateNoteForm = _shouldDisplayCreateNoteForm({ variation });

  return (
    <SrDataTableConnected
      itemsPropName="notes"
      rootViewId={dataTableConfiguration.rootViewId}
      dataSource={dataTableConfiguration.dataSource}
      initialViewSettings={dataTableConfiguration.initialViewSettings}
    >
      {({ notes, reloadItems, dataTableApiState, onInfiniteScrollWrapperRef }) => (
        <>
          <SrBox overflowY="auto" maxHeight="200px">
            <div ref={onInfiniteScrollWrapperRef}>
              <SrNotesList
                type={notesListConfiguration.type}
                theme={notesListConfiguration.theme}
                notes={notes}
                dispatch={dispatch}
                reloadDataTable={(noteId) => {
                  reloadItems({
                    count: 1,
                    filter: { [dataTableConfiguration.filterId]: noteId },
                  });
                }}
                currentUser={currentUser}
                onDeleteNote={onNoteDeleted}
              />
            </div>
          </SrBox>
          {shouldDisplayCreateNoteForm && (
            <SrCreateNoteForm
              listingId={listingId}
              jobOrderId={jobOrderId}
              dispatch={dispatch}
              theme={SR_CREATE_NOTE_FORM_THEME_ADOBE}
              onNoteCreated={onNoteCreated}
              reloadDataTable={(noteId) => {
                reloadItems({
                  count: 1,
                  filter: { [dataTableConfiguration.filterId]: noteId },
                });
              }}
            />
          )}
          {dataTableApiState.inProgress && (
            <SrFlex justify="center">
              <div className="loading-gif" />
            </SrFlex>
          )}
        </>
      )}
    </SrDataTableConnected>
  );
};

SrAdminNotes.propTypes = {
  listingId: PropTypes.number,
  jobOrderId: PropTypes.number,
  variation: PropTypes.oneOf(ADMIN_NOTES_VARIATIONS).isRequired,
  dispatch: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  onNoteCreated: PropTypes.func,
  onNoteDeleted: PropTypes.func,
};

SrAdminNotes.displayName = 'SrAdminNotes';

export default SrAdminNotes;
